import langEn from './lang/lang.en.json';

// 18개 비영어 언어: 코드 → webpack dynamic import. 코드↔파일 매핑은 기존 langMap과 동일.
const LANG_IMPORTERS = {
    kor: () => import(/* webpackChunkName: "lang-kor" */ './lang/lang.ko.json'),
    jpn: () => import(/* webpackChunkName: "lang-jpn" */ './lang/lang.ja.json'),
    cmn: () => import(/* webpackChunkName: "lang-cmn" */ './lang/lang.zh-hans.json'),
    zhh: () => import(/* webpackChunkName: "lang-zhh" */ './lang/lang.zh-hant.json'),
    deu: () => import(/* webpackChunkName: "lang-deu" */ './lang/lang.de.json'),
    spa: () => import(/* webpackChunkName: "lang-spa" */ './lang/lang.es.json'),
    fre: () => import(/* webpackChunkName: "lang-fre" */ './lang/lang.fr.json'),
    ita: () => import(/* webpackChunkName: "lang-ita" */ './lang/lang.it.json'),
    slk: () => import(/* webpackChunkName: "lang-slk" */ './lang/lang.sk.json'),
    pol: () => import(/* webpackChunkName: "lang-pol" */ './lang/lang.pl.json'),
    ron: () => import(/* webpackChunkName: "lang-ron" */ './lang/lang.ro.json'),
    ces: () => import(/* webpackChunkName: "lang-ces" */ './lang/lang.cs.json'),
    por: () => import(/* webpackChunkName: "lang-por" */ './lang/lang.pt.json'),
    swe: () => import(/* webpackChunkName: "lang-swe" */ './lang/lang.sv.json'),
    ell: () => import(/* webpackChunkName: "lang-ell" */ './lang/lang.el.json'),
    tur: () => import(/* webpackChunkName: "lang-tur" */ './lang/lang.tr.json'),
    rus: () => import(/* webpackChunkName: "lang-rus" */ './lang/lang.ru.json'),
    kaz: () => import(/* webpackChunkName: "lang-kaz" */ './lang/lang.kk.json'),
};

export const SUPPORTED_LANGS = ['eng', ...Object.keys(LANG_IMPORTERS)];

const loadedLangs = { eng: langEn };
const pendingLoads = {};
const listeners = [];
const warnedCodes = new Set(); // 미지원 코드 warn 1회 제한 — 렌더 루프 warn 스팸 방지
const failedCodes = new Set();  // 청크 로드 실패 warn 1회 제한 — CDN 장애 시 재렌더 warn 스팸 방지
const warnedLocaleCodes = new Set(); // IMP-438: locale tag 미지원 warn 1회 제한. warnedCodes 와
//   분리한다 — 공유하면 loadLang 이 먼저 warn 한 코드에 대해 locale 쪽 warn 이 영영 안 찍혀
//   두 신호 중 하나가 소실된다(§7 관측성). 사유가 다른 두 경고는 각자 1회씩 남긴다.

// IMP-438: 앱 3글자 코드(ISO 639-3 계열) → BCP-47 locale tag.
// toLocaleString()/toLocaleDateString() 의 locale 인자로 쓰기 위한 정방향 맵.
// preLoginLang.js 의 BROWSER_TO_DB 는 역방향(navigator.language → DB 코드)이며
// zh-cn/zh-sg/zh-hans 등 다대일이라 역산으로 정방향 맵을 만들 수 없다 — 방향이
// 다른 별개 계약이라 아래는 중복이 아니라 신규 정의다.
// 목록은 LANG_IMPORTERS(위) + 'eng' 19종과 반드시 일치해야 한다
// (드리프트 감지 테스트는 별도 담당이 추가).
const LANG_TO_BCP47 = {
    eng: 'en', kor: 'ko', jpn: 'ja', cmn: 'zh-Hans', zhh: 'zh-Hant', deu: 'de',
    spa: 'es', fre: 'fr', ita: 'it', slk: 'sk', pol: 'pl', ron: 'ro', ces: 'cs',
    por: 'pt', swe: 'sv', ell: 'el', tur: 'tr', rus: 'ru', kaz: 'kk',
};

// 앱 언어 코드 → BCP-47 locale tag. 미지원/누락 코드는 반드시 'en' 을 반환한다 —
// undefined 를 toLocaleString/toLocaleDateString 에 넘기면 브라우저 로케일로
// 폴백되어 IMP-438 에서 고치려는 버그(브라우저 로케일 날짜)가 그대로 재현된다.
// 코드당 1회만 warn (렌더 루프 warn 스팸 방지 — 위 warnedCodes 와 동일 이유, Set 은 분리).
export const toBcp47 = (code) => {
    // 언어 미설정 사용자는 resolveCurrentLangCode() 가 **정상 상태로** null 을 반환한다
    // (Translate.js 도 같은 null 을 warn 없이 영어 분기로 처리). 여기서 warn 하면 코드당 1회
    // 제한을 두고 지키려던 관측 신호가 정상 상태 노이즈로 오염된다.
    if (code === null || code === undefined) return 'en';
    // Object.prototype 체인 조회 차단. 'toString'/'constructor'/'__proto__'/'hasOwnProperty' 등이
    // 들어오면 상속 값(함수/객체)이 truthy 로 반환돼 화이트리스트와 warn 을 **모두 우회**한다.
    // 그 값을 toLocaleDateString 에 넘겨도 예외가 나지 않고 조용히 브라우저 로케일로 폴백해,
    // IMP-438 이 고치려는 버그가 그대로 재현된다(node 로 5개 키 재현 확인).
    const tag = Object.prototype.hasOwnProperty.call(LANG_TO_BCP47, code) ? LANG_TO_BCP47[code] : undefined;
    if (tag) return tag;
    if (!warnedLocaleCodes.has(code)) {
        warnedLocaleCodes.add(code);
        console.warn('[langResources] unsupported lang code for locale tag, fallback to en', { code });
    }
    return 'en';
};

export const onLangLoaded = (cb) => {
    listeners.push(cb);
};

export const getLangSync = (code) => loadedLangs[code];

// [IMP-328 규칙] 언어를 변경하는 신규 코드는 반드시 loadLang(code) 완료 후 상태를 반영하라
// (setState/redux 갱신/SPA 이동 게이팅. hard reload 경로는 index.js 부트스트랩이 선로딩 보장).
// Translate()는 동기 함수라 미로드 언어는 영어 fallback으로 렌더되며,
// 청크 로드 완료가 소비 컴포넌트의 재렌더를 트리거하지 않는다.
export const loadLang = (code) => {
    if (loadedLangs[code]) return Promise.resolve(loadedLangs[code]);
    if (!LANG_IMPORTERS[code]) {
        if (!warnedCodes.has(code)) {
            warnedCodes.add(code);
            console.warn('[langResources] unsupported lang code, fallback to eng', { code });
        }
        return Promise.resolve(langEn);
    }
    if (pendingLoads[code]) return pendingLoads[code];
    pendingLoads[code] = LANG_IMPORTERS[code]()
        .then((mod) => {
            const data = mod?.default ?? mod;
            loadedLangs[code] = data;
            delete pendingLoads[code];
            listeners.forEach((cb) => {
                try {
                    cb(code, data);
                } catch (e) {
                    console.warn('[langResources] onLangLoaded listener failed', e);
                }
            });
            return data;
        })
        .catch((e) => {
            // 실패 코드를 loadedLangs 에 캐시하지 않는 것은 의도된 self-heal —
            // 다음 loadLang 호출(재렌더 트리거 등) 때 청크를 재시도할 수 있게 한다.
            // 다만 CDN 장애/오프라인 시 재렌더마다 warn 이 반복되지 않도록 코드당 1회로 제한한다.
            if (!failedCodes.has(code)) {
                failedCodes.add(code);
                console.warn('[langResources] lang chunk load failed, fallback to eng', { code, reason: e?.message });
            }
            delete pendingLoads[code];
            return langEn;
        });
    return pendingLoads[code];
};

export const loadAllLangs = () =>
    Promise.all(SUPPORTED_LANGS.map((c) => loadLang(c).then((d) => [c, d])))
        .then((entries) => Object.fromEntries(entries));
